// oxlint-disable jsx-a11y/control-has-associated-label
import type { SchemaFromJsonResult } from "@schema-benchmarks/bench";
import { formatDuration, getTransitionName, numFormatter } from "@schema-benchmarks/utils";
import { useMemo, useState } from "react";
import { ErrorBoundary } from "react-error-boundary";

import { DownloadCount } from "#src/routes/_benchmarks/-components/count";
import { Snippet } from "#src/routes/_benchmarks/_runtime/-components/table/snippet";
import type { SortableKey } from "#src/routes/_benchmarks/_runtime/-constants";
import { Radio } from "#src/shared/components/radio";
import { Scaler } from "#src/shared/components/scaler";
import { MdSymbol } from "#src/shared/components/symbol";
import { Bar } from "#src/shared/components/table/bar";
import { SortableHeaderLink } from "#src/shared/components/table/sort";
import { useNumberFormatter } from "#src/shared/hooks/format/use-number-formatter";
import type { SortDirection } from "#src/shared/lib/sort";

import "#src/routes/json-schema/_conversion/to-json/-components/table/index.css";

export interface FromJsonTableProps {
  results: Array<SchemaFromJsonResult>;
  meanScaler: ReturnType<typeof Bar.getScale>;
  sortBy: SortableKey;
  sortDir: SortDirection;
}

const getRatio = (a: number, b: number) => {
  if (a === b) return 0;
  if (a < b) return -(b / a);
  return a / b;
};

const getDefaultCompareId = (results: Array<SchemaFromJsonResult>) => results[0]?.id;

function useComparison(results: Array<SchemaFromJsonResult>) {
  const [selectedCompareId, setSelectedCompareId] = useState<string | undefined>(undefined);
  const resultsById = useMemo(() => {
    return Object.fromEntries(results.map((result) => [result.id, result]));
  }, [results]);
  const defaultCompareId = getDefaultCompareId(results);
  const compareId =
    selectedCompareId && resultsById[selectedCompareId] ? selectedCompareId : defaultCompareId;
  const compareResult = compareId && resultsById[compareId];
  const ratioScaler = useMemo(() => {
    if (!compareResult) return undefined;
    const ratios = results.map((result) => getRatio(result.mean, compareResult.mean));
    const max = Math.max(...ratios);
    const min = Math.min(...ratios);
    return Scaler.getScale([min, max, -min, -max], {
      type: "stat",
      lowerBetter: true,
    });
  }, [results, compareResult]);
  return { compareId, setCompareId: setSelectedCompareId, compareResult, ratioScaler };
}

export function FromJsonTable({ results, meanScaler, ...sortState }: FromJsonTableProps) {
  const { compareId, setCompareId, compareResult, ratioScaler } = useComparison(results);
  const formatNumber = useNumberFormatter(numFormatter);
  const showComparisonColumns = results.length > 1;
  return (
    <table
      className="json-schema-table"
      aria-label="Results"
      style={{ viewTransitionName: "result-table" }}
    >
      <thead>
        <tr>
          <SortableHeaderLink
            {...SortableHeaderLink.getProps("libraryName", sortState, {
              to: "/json-schema/from-json",
            })}
          >
            Library
          </SortableHeaderLink>
          <th className="action"></th>
          <th>Version</th>
          <SortableHeaderLink
            {...SortableHeaderLink.getProps(
              "downloads",
              sortState,
              { to: "/json-schema/from-json" },
              "descending",
            )}
            className="numeric"
            aria-label="Downloads per week"
          >
            <span className="json-schema-table__downloads-label">
              <MdSymbol size={18}>download</MdSymbol>/wk
            </span>
          </SortableHeaderLink>
          <SortableHeaderLink
            {...SortableHeaderLink.getProps("mean", sortState, { to: "/json-schema/from-json" })}
            className="numeric"
          >
            Mean
          </SortableHeaderLink>
          {showComparisonColumns && (
            <>
              <th className="bar-after"></th>
              <th className="fit-content action" colSpan={2}>
                Compare
              </th>
            </>
          )}
        </tr>
      </thead>
      <tbody>
        {results.map((result) => {
          const ratio = compareResult && getRatio(result.mean, compareResult.mean);
          return (
            <tr
              key={result.id}
              style={{
                viewTransitionName: getTransitionName("json-schema-table-row", {
                  libraryName: result.libraryName,
                  note: result.note,
                }),
              }}
            >
              <td>
                <code className="language-text">{result.libraryName}</code>
                {result.note ? ` (${result.note})` : null}
              </td>
              <td className="action">
                <Snippet code={result.snippet} />
              </td>
              <td>
                <code className="language-text">{result.version}</code>
              </td>
              <td className="numeric">
                <ErrorBoundary fallback={null}>
                  <DownloadCount libraryName={result.libraryName} />
                </ErrorBoundary>
              </td>
              <td className="numeric">{formatDuration(result.mean)}</td>
              {showComparisonColumns && (
                <td className="bar-after">
                  <Bar {...meanScaler(result.mean)} />
                </td>
              )}
              {showComparisonColumns && (
                <>
                  <td className="fit-content action">
                    <Radio
                      name="compare"
                      value={result.id}
                      checked={compareId === result.id}
                      onChange={(event) => {
                        setCompareId(event.target.checked ? result.id : undefined);
                      }}
                    />
                  </td>
                  <td className="numeric bar-after">
                    {compareResult &&
                      ratioScaler &&
                      compareId !== result.id &&
                      (ratio ? (
                        <Scaler
                          {...ratioScaler(ratio)}
                          symbolLabel={`${ratio > 0 ? "Slower" : "Faster"} than ${compareResult.libraryName}${compareResult.note ? ` (${compareResult.note})` : ""}`}
                        >
                          <span aria-label={`${numFormatter.format(Math.abs(ratio))}x`}>
                            {`${formatNumber(Math.abs(ratio))}x`}
                          </span>
                        </Scaler>
                      ) : (
                        <Scaler
                          icon={<MdSymbol>stat_0</MdSymbol>}
                          color="var(--yellow)"
                          symbolLabel="Equal"
                        >
                          1x
                        </Scaler>
                      ))}
                  </td>
                </>
              )}
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}
